import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  IvrComposeRequest,
  IvrDetailResponse,
  IvrListRow,
  IvrSettingRow,
  IvrUpdateRequest,
} from './types';

export const ivrKeys = {
  all: ['ivr'] as const,
  list: (userId: string | number | null) => ['ivr', 'list', userId] as const,
  detail: (id: string | number) => ['ivr', 'detail', id] as const,
  settings: ['ivr', 'settings'] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ivrKeys.all });
}

/** GET /api/obd/custom/ivr/list/{userId} */
export function useIvrList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ivrKeys.list(userId),
    queryFn: () => api.get<IvrListRow[]>(`/api/obd/custom/ivr/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<IvrListRow>(data),
  });
}

/** GET /api/obd/get/custom/ivr/{ivrId} */
export function useIvrDetail(ivrId: string | number | null) {
  return useQuery({
    queryKey: ivrKeys.detail(ivrId ?? 'none'),
    queryFn: () => api.get<IvrDetailResponse>(`/api/obd/get/custom/ivr/${ivrId}`),
    enabled: Boolean(ivrId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/ivr/settings — verb palette (action → display name). */
export function useIvrSettings() {
  return useQuery({
    queryKey: ivrKeys.settings,
    queryFn: () => api.get<IvrSettingRow[]>('/api/obd/ivr/settings'),
    staleTime: 5 * 60_000,
    select: (data) => ensureArray<IvrSettingRow>(data),
  });
}

/** POST /api/obd/add/custom/ivr */
export function useCreateIvr() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: IvrComposeRequest) =>
      api.post<{ message?: string }>('/api/obd/add/custom/ivr', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/update/custom/ivr */
export function useUpdateIvr() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: IvrUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/update/custom/ivr', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/custom/ivr/{ivrId} with method DELETE override (legacy). */
export function useDeleteIvr() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (ivrId: number) =>
      apiClient
        .delete<{ message?: string }>(`/api/obd/custom/ivr/${ivrId}`)
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}
